import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PostHogProvider } from '@posthog/react';
import './index.css';
import App from './App';
import { getPostHogConfig, getPostHogInitOptions } from './config/posthog';

const posthogConfig = getPostHogConfig();
const posthogOptions = getPostHogInitOptions();

// Global error handlers for better mobile debugging
if (typeof window !== 'undefined') {
  // After a deploy, users with a stale index.html may request chunk files whose hashed
  // names no longer exist. The server now 404s those, but webpack/React.lazy then throw
  // ChunkLoadError. Reload once to pick up the new index.html and the new chunk names.
  // The session flag prevents reload loops if the failure is a real network error.
  const RELOAD_FLAG = 'mentis:chunk-reloaded';
  const isStaleChunkError = (err: unknown, message?: string): boolean => {
    const name = (err as { name?: string } | undefined)?.name || '';
    const msg = ((err as { message?: string } | undefined)?.message || message || '').toString();
    return (
      name === 'ChunkLoadError' ||
      /Loading chunk [\w-]+ failed/i.test(msg) ||
      /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
      /Unexpected token '?</.test(msg)
    );
  };
  const tryRecoverFromStaleChunk = (err: unknown, message?: string) => {
    if (!isStaleChunkError(err, message)) return false;
    try {
      if (sessionStorage.getItem(RELOAD_FLAG) === '1') return false;
      sessionStorage.setItem(RELOAD_FLAG, '1');
    } catch {
      // sessionStorage unavailable (private mode etc.) — fall through and reload anyway.
    }
    window.location.reload();
    return true;
  };

  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    if (tryRecoverFromStaleChunk(event.reason)) return;
    event.preventDefault();
  });

  window.addEventListener('error', (event) => {
    console.error('Global error caught:', event.error || event.message);
    console.error('Error source:', event.filename, 'Line:', event.lineno);
    tryRecoverFromStaleChunk(event.error, event.message);
  });
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Only render PostHog provider if API key is available
const AppWithPostHog = posthogConfig.apiKey ? (
  <PostHogProvider 
    apiKey={posthogConfig.apiKey} 
    options={posthogOptions}
  >
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <App />
    </BrowserRouter>
  </PostHogProvider>
) : (
  <BrowserRouter
    future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }}
  >
    <App />
  </BrowserRouter>
);

root.render(
  <React.StrictMode>
    {AppWithPostHog}
  </React.StrictMode>
);
